import {forwardRef, memo, Ref, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  SxProps,
  Theme,
  Typography,
  Tooltip,
  Slide,
  Grid,
  Skeleton,
  FormGroup,
  Checkbox,
} from '@mui/material';
import {AccessAlarmOutlined, Add, CloseOutlined} from '@mui/icons-material';
import moment, {isMoment} from 'moment';
import {
  Control,
  Controller,
  useController,
  useFieldArray,
  useForm,
  UseFormReturn,
  useFormState,
  useWatch,
} from 'react-hook-form';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {ScheduleField} from '../schedule-field/schedule-field';
import {FlatInputProps} from '../flat-input/flat-input';
import {FlatNumericInput} from '../flat-numeric-input';
import {Modal} from '../modal/modal';
import {DynamicFormFieldShape} from '../dynamic-form';
import {FlatSelectDropdown, Option} from '../flat-select-dropdown/flat-select-dropdown';
import {FlatStartsAtTime} from '../flat-starts-at-time/flat-starts-at-time';
import {FlatStartsAtDate} from '../flat-starts-at-date/flat-starts-at-date';
import {EntityActivitiesRepository} from '../../repositories/entity-activities';
import {PosologySchedule} from '../../backend/enums/posology-schedule';
import {DEFAULT_FORM_VALIDATIONS} from '../../utils/constants';
import {
  Frequency,
  PeriodsOfTheDaySchedule,
  RepetitionSchedule,
  SchedulerTypes,
  TimeListSchedule,
  PeriodOfTheDayItem,
  TimeListItem,
  PosologyScheduleFormType,
} from '../../types';
import {sortTimesListItems} from '../../utils/functions/sort-times-list-items';
import {getDaysOfTheWeek} from '../../utils/functions/days-of-the-week';

export type ActivityTypePosologyScheduleFormProps = FlatInputProps & {
  type: SchedulerTypes.ActivityType;
  open: boolean;
  onClose: () => void;
  updateDisplay: (data: any) => void;
  startAtTimeId: string;
  startAtDateId: string;
  readonly?: boolean;
  parentFormInstance: UseFormReturn<DynamicFormFieldShape>;
  activitySectorId: string;
  activityGroupId: string;
};

export const ActivityTypePosologyScheduleForm = forwardRef(
  (props: ActivityTypePosologyScheduleFormProps, ref: Ref<HTMLInputElement>) => {
    // trigger nested schedule modal
    const [openSchedule, setOpenSchedule] = useState(false);

    // flag add more
    const [addTimeListItem, setAddTimeListItem] = useState(false);

    const [valuesPrefilled, setValuesPrefilled] = useState(false);

    // store pending value out of the form values
    const [pendingTimeListItem, setPendingTimeListItem] = useState<TimeListItem | undefined>(
      undefined
    );

    const {t} = useTranslation();

    const {parentFormInstance, activitySectorId, activityGroupId, ...restProps} = props;

    // Remove startAtTimeId and startAtDateId from restProps before passing to ScheduleField
    const {startAtTimeId, startAtDateId, ...filteredRestProps} = restProps;

    const {entityId}: {entityId: string} = useParams();

    // outer parent form values for validations
    const [startAtDateValue, startAtTimeValue]: [
      startAtDateValue?: string,
      startAtTimeValue?: string,
    ] = parentFormInstance.getValues([props.startAtDateId, props.startAtTimeId]);

    const startsAtDateTime = useMemo(
      () =>
        moment(startAtDateValue)
          .clone()
          .set({
            hour: moment(startAtTimeValue).hour(),
            minute: moment(startAtTimeValue).minute(),
          }),
      [startAtDateValue, startAtTimeValue]
    );

    const defaultFormValues = useMemo(
      (): PosologyScheduleFormType => ({
        posologyScheduleType: PosologySchedule.PeriodsOfTheDay,
        frequency: Frequency.Daily,
        frequencyValue: 1,
        repetitionQuantity: 1,
        timesList: [],
        periodsOfTheDay: [],
        endDate: startsAtDateTime
          ? startsAtDateTime.clone().add(1, 'day').toString()
          : moment().add(2, 'days').toString(),
        endHour: startsAtDateTime ? startsAtDateTime.toString() : moment().toString(),
      }),
      [startsAtDateTime]
    );

    // enqueue react-hook-form
    const {reset, control, getValues, setValue, setError, clearErrors, trigger} =
      useForm<PosologyScheduleFormType>({
        defaultValues: defaultFormValues,
        ...DEFAULT_FORM_VALIDATIONS,
      });

    const PosologyScheduleTypeValue = useWatch({
      control,
      name: 'posologyScheduleType',
      defaultValue: defaultFormValues.posologyScheduleType,
    });
    const frequency = useWatch({control, name: 'frequency'});
    const endDateValue = useWatch({control, name: 'endDate'});
    const frequencyValue = useWatch({control, name: 'frequencyValue'});
    const repetitionQuantityValue = useWatch({control, name: 'repetitionQuantity'});
    const watchedTimesList = useWatch({control, name: 'timesList'});
    const watchedPeriodsOfTheDay = useWatch({control, name: 'periodsOfTheDay'});
    const liveTimesList = useMemo(() => watchedTimesList || [], [watchedTimesList]);
    const livePeriodsOfTheDay = useMemo(
      () => watchedPeriodsOfTheDay || [],
      [watchedPeriodsOfTheDay]
    );
    const sortedTimesList = useMemo(() => {
      return liveTimesList
        .map((timeSlot, index) => ({
          index,
          ...timeSlot,
        }))
        .sort(sortTimesListItems);
    }, [liveTimesList]);

    const {errors} = useFormState({
      control,
      name: [
        'periodsOfTheDay',
        'timesList',
        'frequency',
        'frequencyValue',
        'endDate',
        'endHour',
        'repetitionQuantity',
      ],
    });

    const {
      append: appendItemToTimesList,
      remove: removeItemFromTimesList,
      replace: replaceTimesList,
    } = useFieldArray({
      name: 'timesList',
      control,
      rules: {
        required: {
          value: PosologyScheduleTypeValue === PosologySchedule.TimesList,
          message: t(translations.activity_type_posology_schedule_form.fields.time_list.required),
        },
        validate: {
          validateArrayEmptiness: (value) => {
            if (PosologyScheduleTypeValue !== PosologySchedule.TimesList) return true;
            return (
              (value && value?.length > 0) ||
              t(translations.activity_type_posology_schedule_form.fields.time_list.required)
            );
          },
        },
      },
    });

    const {replace: replacePeriodsOfTheDay} = useFieldArray({
      name: 'periodsOfTheDay',
      control,
      rules: {
        required: {
          value: PosologyScheduleTypeValue === PosologySchedule.PeriodsOfTheDay,
          message: t(
            translations.activity_type_posology_schedule_form.fields.periods_of_the_day_item
              .required
          ),
        },
        validate: {
          validateArrayEmptiness: (value) => {
            if (PosologyScheduleTypeValue !== PosologySchedule.PeriodsOfTheDay) return true;
            return (
              value?.length > 0 ||
              t(
                translations.activity_type_posology_schedule_form.fields.periods_of_the_day_item
                  .required
              )
            );
          },
          validateAtLeastOneQuantityOverZero: (value) => {
            if (
              value?.length > 0 &&
              PosologyScheduleTypeValue === PosologySchedule.PeriodsOfTheDay
            ) {
              return (
                value.some((v) => v.quantity > 0) ||
                t(
                  translations.activity_type_posology_schedule_form.fields.periods_of_the_day_item
                    .required
                )
              );
            }

            return true;
          },
        },
      },
    });

    // Load all activities groups
    const {items: activityGroupTypes, isLoading: isLoadingActivityGroupTypes} = useFetchAllItems({
      fetchFunction: ({limit, skip}) => {
        return EntityActivitiesRepository.instance.getTypes(
          skip,
          limit,
          entityId,
          activitySectorId,
          activityGroupId
        );
      },
      enabled: openSchedule,
    });

    const posologyOptions: Option[] = useMemo(
      () =>
        Object.values(PosologySchedule).map((type: PosologySchedule) => ({
          key: type,
          label: t(
            translations.activity_type_posology_schedule_form.fields.posology_schedule_type.options[
              type
            ]
          ),
        })),
      [t]
    );

    const frequencyOptions: Option[] = useMemo(() => {
      const options: Option[] = Object.values(Frequency).map((type: Frequency) => {
        return {
          key: type,
          label: t(
            translations.activity_type_posology_schedule_form.fields.frequency.options[type]
          ),
        };
      });

      if (
        PosologyScheduleTypeValue === PosologySchedule.PeriodsOfTheDay ||
        PosologyScheduleTypeValue === PosologySchedule.TimesList
      )
        return options.filter((option) => option.key !== Frequency.Hourly);

      if (PosologyScheduleTypeValue === PosologySchedule.Repetition)
        return options.filter((option) => option.key === Frequency.Hourly);

      return options;
    }, [PosologyScheduleTypeValue, t]);

    const frequencyValuesRules = useMemo(() => {
      if (frequency === Frequency.Hourly) {
        return {
          min: 1,
          max: 24,
        };
      }
      if (frequency === Frequency.Daily) {
        return {
          min: 1,
          max: 7,
        };
      }
      if (frequency === Frequency.Weekly) {
        return {
          min: 1,
          max: 52,
        };
      }
      return {min: 1, max: 1};
    }, [frequency]);

    const timeListErrorMessage = errors.timesList?.message || errors.timesList?.root?.message;
    const periodsOfTheDayErrorMessage =
      errors.periodsOfTheDay?.message || errors.periodsOfTheDay?.root?.message;
    const frequencyValueErrorMessage = errors.frequencyValue?.message;
    const frequencyErrorMessage = errors.frequency?.message;
    const endDateErrorMessage = errors.endDate?.message;
    const endHourErrorMessage = errors.endHour?.message;
    const repetitionQuantityErrorMessage = errors.repetitionQuantity?.message;

    const hasPeriodsOfTheDayQuantity = useMemo(
      () => livePeriodsOfTheDay.some((periodOfTheDay) => Number(periodOfTheDay?.quantity || 0) > 0),
      [livePeriodsOfTheDay]
    );

    const timeListSlots = useMemo(
      () => new Set(liveTimesList.map((timeSlot) => moment(timeSlot.time).format('LT'))),
      [liveTimesList]
    );

    const frequencyValueNumber = Number(frequencyValue);
    const repetitionQuantityNumber = Number(repetitionQuantityValue);
    const frequencyValueIsValid =
      Number.isFinite(frequencyValueNumber) &&
      frequencyValueNumber >= frequencyValuesRules.min &&
      frequencyValueNumber <= frequencyValuesRules.max;
    const repetitionQuantityIsValid =
      Number.isFinite(repetitionQuantityNumber) && repetitionQuantityNumber >= 0;

    const formNotValid = useMemo(() => {
      if (!PosologyScheduleTypeValue) return true;

      if (endDateErrorMessage || endHourErrorMessage) return true;

      if (PosologyScheduleTypeValue === PosologySchedule.PeriodsOfTheDay) {
        return (
          !hasPeriodsOfTheDayQuantity ||
          Boolean(periodsOfTheDayErrorMessage) ||
          Boolean(frequencyErrorMessage) ||
          Boolean(frequencyValueErrorMessage) ||
          !frequencyValueIsValid
        );
      }

      if (PosologyScheduleTypeValue === PosologySchedule.TimesList) {
        return (
          liveTimesList.length === 0 ||
          Boolean(timeListErrorMessage) ||
          Boolean(frequencyErrorMessage) ||
          Boolean(frequencyValueErrorMessage) ||
          !frequencyValueIsValid
        );
      }

      if (PosologyScheduleTypeValue === PosologySchedule.Repetition) {
        return (
          Boolean(frequencyValueErrorMessage) ||
          Boolean(repetitionQuantityErrorMessage) ||
          !frequencyValueIsValid ||
          !repetitionQuantityIsValid
        );
      }

      return false;
    }, [
      PosologyScheduleTypeValue,
      endDateErrorMessage,
      endHourErrorMessage,
      hasPeriodsOfTheDayQuantity,
      periodsOfTheDayErrorMessage,
      frequencyErrorMessage,
      frequencyValueErrorMessage,
      frequencyValueIsValid,
      liveTimesList.length,
      timeListErrorMessage,
      repetitionQuantityErrorMessage,
      repetitionQuantityIsValid,
    ]);

    const clearTimesListState = useCallback(() => {
      replaceTimesList([]);
      clearErrors('timesList');
      setAddTimeListItem(false);
      setPendingTimeListItem(undefined);
    }, [clearErrors, replaceTimesList]);

    const clearPeriodsOfTheDayState = useCallback(() => {
      clearErrors('periodsOfTheDay');
    }, [clearErrors]);

    const handleModalClose = () => {
      // reset form
      reset(defaultFormValues);

      // reset array items
      replacePeriodsOfTheDay([]);
      clearTimesListState();

      // close schedule options modal
      setOpenSchedule(false);
      setValuesPrefilled(false);
    };

    const onSubmit = () => {
      // get instance values
      const values = getValues();

      let updatedData: PosologyScheduleFormType = {
        frequency: values.frequency,
        frequencyValue:
          values.posologyScheduleType === PosologySchedule.Repetition ||
          values.posologyScheduleType === PosologySchedule.TimesList ||
          values.posologyScheduleType === PosologySchedule.PeriodsOfTheDay
            ? values.frequencyValue
            : 1,
        endDate: values.endDate,
        endHour: values.endHour,
        posologyScheduleType: values.posologyScheduleType,
        timesList:
          values.posologyScheduleType === PosologySchedule.TimesList ? values.timesList || [] : [],
      };

      // Include days field when frequency is weekly
      if (values.frequency === Frequency.Weekly) {
        updatedData = {
          ...updatedData,
          days: values.days && values.days.length > 0 ? values.days : undefined,
        };
      }

      if (values.posologyScheduleType === PosologySchedule.TimesList) {
        updatedData = {
          ...updatedData,
          timesList: values.timesList || [],
        } as TimeListSchedule;
      }

      if (values.posologyScheduleType === PosologySchedule.PeriodsOfTheDay) {
        updatedData = {
          ...updatedData,
          periodsOfTheDay: values.periodsOfTheDay
            ? values.periodsOfTheDay.filter((item) => item.quantity !== 0)
            : [],
        } as PeriodsOfTheDaySchedule;
      }

      if (values.posologyScheduleType === PosologySchedule.Repetition) {
        updatedData = {
          ...updatedData,
          frequency: Frequency.Hourly, // Always set to hourly for repetition
          repetitionQuantity: values.repetitionQuantity,
        } as RepetitionSchedule;
      }

      // update upstream form
      parentFormInstance.setValue(props.id ?? '', JSON.stringify(updatedData), {
        shouldDirty: true,
        shouldValidate: true,
      });

      // side-effect close
      handleModalClose();
    };

    // prefill form values async
    const prefillAsyncFormValues = useCallback(() => {
      if (openSchedule) {
        let prefilledValues: PosologyScheduleFormType = props.value
          ? JSON.parse(String(props.value))
          : undefined;

        if (prefilledValues) {
          reset({...defaultFormValues, ...prefilledValues});
        } else {
          reset(defaultFormValues);
        }
      }
    }, [defaultFormValues, openSchedule, props.value, reset]);

    // prefill form values for activity types
    const prefillAsyncActivityTypesFormValues = useCallback(() => {
      if (activityGroupTypes) {
        let prefilledValues: PosologyScheduleFormType = props.value
          ? JSON.parse(String(props.value))
          : undefined;

        replacePeriodsOfTheDay(
          activityGroupTypes.map((activity): PeriodOfTheDayItem => {
            const preffilledActivity = prefilledValues?.periodsOfTheDay?.find(
              ({activityTypeId}) => activityTypeId === activity.id!
            );

            return {activityTypeId: activity.id!, quantity: preffilledActivity?.quantity || 0};
          })
        );

        setValuesPrefilled(true);
      }
    }, [activityGroupTypes, props.value, replacePeriodsOfTheDay]);

    const genericSchedulerValidations = useCallback(() => {
      let validationErrors = 0;

      const [endHour, endDate, frequency, frequencyValue] = getValues([
        'endHour',
        'endDate',
        'frequency',
        'frequencyValue',
      ]);

      // validate against outter start hour and `frequency`
      if (startAtTimeValue && startAtDateValue) {
        // Create full start datetime combining date and time
        const startDateTime = moment(startAtDateValue)
          .clone()
          .set({
            hour: moment(startAtTimeValue).hour(),
            minute: moment(startAtTimeValue).minute(),
            second: 0,
            millisecond: 0,
          });

        // Create full end datetime combining end date and end hour
        const endDateTime = moment(endDate)
          .clone()
          .set({
            hour: moment(endHour).hour(),
            minute: moment(endHour).minute(),
            second: 0,
            millisecond: 0,
          });

        if (startDateTime.isSameOrAfter(endDateTime) && frequency === Frequency.Hourly) {
          setError('endHour', {
            message: t(
              translations.periodicity_form.fields.end_date.validations.superior_to_start_date
            ),
          });
          validationErrors++;
        } else {
          clearErrors('endHour');
        }
      }

      // validate dates agains repetition frequency
      const minimumPossibleEndDate = moment(startAtDateValue).add({
        ...(frequency === Frequency.Daily && {
          days: frequencyValue,
        }),
        ...(frequency === Frequency.Weekly && {
          weeks: frequencyValue,
        }),
      });

      if (moment(endDate).isBefore(minimumPossibleEndDate)) {
        setError('endDate', {
          message: t(
            translations.activity_type_posology_schedule_form.fields.end_date.validations.min
          ),
        });
        validationErrors++;
      } else {
        clearErrors('endDate');
      }

      return validationErrors === 0;
    }, [clearErrors, getValues, setError, startAtDateValue, startAtTimeValue, t]);

    // watch external start date
    const startAtDateWatch = parentFormInstance.watch(props.startAtDateId);

    // re-validate on `startDate` change against `endDate` on repetition
    useEffect(() => {
      if (isMoment(startAtDateWatch)) {
        const scheduleValue = props.value;

        try {
          const parsedValue = JSON.parse(String(scheduleValue));

          // if repetition has `endDate` defined
          if (parsedValue.endDate) {
            // run against
            if (startAtDateWatch.isAfter(moment(parsedValue.endDate))) {
              parentFormInstance.setError(props.startAtDateId, {
                message: t(
                  translations.periodicity_form.fields.start_at_date.validations
                    .superior_to_end_date
                ),
              });
            } else {
              parentFormInstance.clearErrors(props.startAtDateId);
            }
          }
        } catch (err) {
          // ignore if non-parseable
        }
      }
    }, [endDateValue, parentFormInstance, props.startAtDateId, props.value, startAtDateWatch, t]);

    useEffect(() => {
      if (openSchedule) {
        // prefill async controlled values on open
        prefillAsyncFormValues();
      } else {
        // clean up state on close
        reset(defaultFormValues);
        setAddTimeListItem(false);
        setPendingTimeListItem(undefined);
      }

      return () => {
        reset(defaultFormValues);
        setAddTimeListItem(false);
        setPendingTimeListItem(undefined);
      };
    }, [defaultFormValues, openSchedule, prefillAsyncFormValues, reset]);

    useEffect(() => {
      if (activityGroupTypes && openSchedule) prefillAsyncActivityTypesFormValues();
    }, [openSchedule, activityGroupTypes, prefillAsyncActivityTypesFormValues]);

    const periodsOfTheDayWrapper = useMemo(
      (): SxProps<Theme> => ({
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        width: '100%',
        minHeight: '200px',
        justifyContent: isLoadingActivityGroupTypes ? 'center' : 'space-between',
        alignItems: 'center',
        rowGap: '16px',
      }),
      [isLoadingActivityGroupTypes]
    );

    const hasLoaded =
      !isLoadingActivityGroupTypes &&
      activityGroupTypes &&
      activityGroupTypes?.length &&
      valuesPrefilled;

    return (
      <Box sx={{width: '100%'}}>
        <ScheduleField
          {...filteredRestProps}
          openSchedule={() => setOpenSchedule(true)}
          ref={ref}
        />
        <Modal
          title={t(translations.activity_type_posology_schedule_form.title)}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: handleModalClose,
            },
            {
              label: t(translations.modal.save),
              type: 'neutral',
              disabled: formNotValid,
              onClick: onSubmit,
            },
          ]}
          open={openSchedule}
          PaperProps={{
            sx: modalSx,
          }}
        >
          {!hasLoaded ? (
            <Grid container spacing={4}>
              {new Array(6).fill(1).map((_item: number, index: number) => (
                <Grid item xs={6} key={`skeleton_posology_schedule_${index}`}>
                  <Stack gap={2} display="flex" flexDirection="column">
                    <Skeleton animation="wave" variant="rounded" width={120} height={20} />
                    <Skeleton animation="wave" variant="rounded" width="100%" height={42} />
                  </Stack>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack component="form" sx={modalContentWrapperSx}>
              {/* DEBUGGER */}
              {/* <DevTool control={control} /> */}
              {/**
               * Type selection
               */}
              <Controller
                name="posologyScheduleType"
                control={control}
                defaultValue={PosologySchedule.PeriodsOfTheDay}
                render={({field}) => {
                  const handlePosologyScheduleTypeChange = (option: Option) => {
                    const value = option.key as PosologySchedule;

                    field.onChange(value);

                    if (
                      (value === PosologySchedule.PeriodsOfTheDay ||
                        value === PosologySchedule.TimesList) &&
                      frequency === Frequency.Hourly
                    ) {
                      setValue('frequency', Frequency.Daily, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }

                    // Set frequency to Hourly when Repetition is selected
                    if (value === PosologySchedule.Repetition) {
                      setValue('frequency', Frequency.Hourly, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                  };

                  return (
                    <FlatSelectDropdown
                      onChange={handlePosologyScheduleTypeChange}
                      value={posologyOptions.find((option) => option.key === field.value)}
                      label={t(
                        translations.activity_type_posology_schedule_form.fields
                          .posology_schedule_type.label
                      )}
                      options={posologyOptions}
                    />
                  );
                }}
              />

              {/**
               * Options for PeriodsOfTheDay
               */}
              <Collapse
                in={PosologyScheduleTypeValue === PosologySchedule.PeriodsOfTheDay}
                orientation="vertical"
                unmountOnExit
                mountOnEnter
              >
                <Stack sx={periodsOfTheDayWrapper}>
                  {activityGroupTypes?.map((activity, index) => {
                    const reactKey = `col_${activity.id!}_${index}`;

                    return (
                      <PeriodsOfTheDayQuantityField
                        key={reactKey}
                        activity={activity}
                        index={index}
                        control={control}
                        hasError={Boolean(periodsOfTheDayErrorMessage)}
                        clearSectionError={clearPeriodsOfTheDayState}
                      />
                    );
                  })}
                </Stack>

                {/* <Slide in={Boolean(periodsOfTheDayErrorMessage)}>
                  <Typography component="label" sx={errorMessageSx}>
                    {periodsOfTheDayErrorMessage}
                  </Typography>
                </Slide> */}
              </Collapse>
              {/**
               * Options for TimesList
               */}
              <Collapse
                in={PosologyScheduleTypeValue === PosologySchedule.TimesList}
                orientation="vertical"
                unmountOnExit
                mountOnEnter
              >
                <Stack gap={1} flexDirection="column" alignItems="flex-start">
                  {/**
                   * Add hour to list
                   */}
                  {liveTimesList.length > 0 && (
                    <Stack gap={1} flexDirection="row" flexWrap="wrap">
                      {sortedTimesList.map((timeSlot) => {
                        return (
                          <Chip
                            key={`time_${timeSlot.index}`}
                            label={`${moment(timeSlot.time).format('LT')} - ${timeSlot.quantity}`}
                            icon={<AccessAlarmOutlined fontSize="small" />}
                            deleteIcon={<CloseOutlined fontSize="small" />}
                            onDelete={() => removeItemFromTimesList(timeSlot.index)}
                            sx={timesListChipSx}
                          />
                        );
                      })}
                    </Stack>
                  )}
                  <Collapse in={addTimeListItem}>
                    <Stack flexDirection="row" gap="26px" alignItems="center">
                      <Stack flexDirection="row" gap="6px" alignItems="center">
                        <Stack flexDirection="row" gap={1} alignItems="center">
                          <AccessAlarmOutlined fontSize="small" sx={hourIconSx} />
                          <Typography sx={inputLabelSx}>
                            {t(
                              translations.activity_type_posology_schedule_form.fields.time_list
                                .hour.label
                            )}
                          </Typography>
                        </Stack>
                        <FlatStartsAtTime
                          // @ts-ignore
                          value={pendingTimeListItem?.time || moment()}
                          onChange={(value) => {
                            const updatedPendingTimeListItem: TimeListItem = {
                              time: moment(value).toISOString(),
                              quantity: Number(pendingTimeListItem?.quantity),
                            };

                            setPendingTimeListItem(updatedPendingTimeListItem);

                            const timeSlotExits = timeListSlots.has(moment(value).format('LT'));

                            // Handle errors
                            if (!timeSlotExits) {
                              timeListErrorMessage && clearErrors('timesList');
                            } else {
                              setError('timesList', {
                                message: t(
                                  translations.activity_type_posology_schedule_form.fields.time_list
                                    .error
                                ),
                              });
                            }
                          }}
                        />
                      </Stack>

                      <Stack
                        flexDirection="row"
                        gap="6px"
                        alignItems="center"
                        sx={{width: '153px'}}
                      >
                        <Typography sx={inputLabelSx}>
                          {t(
                            translations.activity_type_posology_schedule_form.fields.time_list
                              .quantity.label
                          )}
                        </Typography>

                        <FlatNumericInput
                          min={0}
                          max={99}
                          step={0.5}
                          value={pendingTimeListItem?.quantity}
                          onChange={(e, value) => {
                            const quantity = Number(value);

                            if (quantity >= 0.5) {
                              const updatedPendingTimeListItem: TimeListItem = {
                                time: pendingTimeListItem?.time || moment().toISOString(),
                                quantity: Number(value),
                              };

                              setPendingTimeListItem(updatedPendingTimeListItem);
                            }
                          }}
                          sx={quantityInputSx}
                        />
                      </Stack>

                      <Button
                        disabled={!pendingTimeListItem || Boolean(timeListErrorMessage)}
                        onClick={() => {
                          if (pendingTimeListItem) {
                            const timeSlotExits = timeListSlots.has(
                              moment(pendingTimeListItem.time).format('LT')
                            );

                            // Handle errors
                            if (!timeSlotExits) {
                              // Clear errors if any
                              timeListErrorMessage && clearErrors('timesList');

                              // Flag display
                              setAddTimeListItem(false);

                              // Append to list
                              appendItemToTimesList({
                                time: pendingTimeListItem?.time,
                                quantity: pendingTimeListItem?.quantity,
                              });

                              // trigger validation
                              trigger('timesList');

                              // Clear state
                              setPendingTimeListItem(undefined);
                            } else {
                              setError('timesList', {
                                message: t(
                                  translations.activity_type_posology_schedule_form.fields.time_list
                                    .error
                                ),
                              });
                            }
                          }
                        }}
                        sx={{
                          textDecoration: 'underline',
                        }}
                      >
                        {t(translations.office.generics.save)}
                      </Button>
                    </Stack>

                    <Slide in={Boolean(timeListErrorMessage)}>
                      <Typography component="label" sx={errorMessageSx}>
                        {timeListErrorMessage}
                      </Typography>
                    </Slide>
                  </Collapse>

                  <Collapse in={!addTimeListItem}>
                    <Button
                      startIcon={<Add />}
                      onClick={() => {
                        setAddTimeListItem(true);
                        setPendingTimeListItem({time: moment().toISOString(), quantity: 1});
                      }}
                    >
                      {t(translations.activity_type_posology_schedule_form.fields.time_list.add)}
                    </Button>
                  </Collapse>
                </Stack>
              </Collapse>
              {/**
               * Options for Repetition
               */}
              <Collapse
                in={PosologyScheduleTypeValue === PosologySchedule.Repetition}
                orientation="vertical"
                unmountOnExit
                mountOnEnter
              >
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  sx={{
                    mb: '22px',
                  }}
                >
                  <Stack
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    flex={1}
                    alignItems="center"
                    maxWidth="50%"
                    position="relative"
                  >
                    <Typography sx={{...inputLabelSx, flex: 1}}>
                      {t(
                        translations.activity_type_posology_schedule_form.fields
                          .repetitionHourlyFrequencyValue
                      )}
                    </Typography>
                    <Controller
                      name="frequencyValue"
                      defaultValue={1}
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t(
                            translations.periodicity_form.fields.frequency_value.validations
                              .required
                          ),
                        },
                        min: frequencyValuesRules.min
                          ? {
                              value: frequencyValuesRules.min,
                              message: t(
                                translations.periodicity_form.fields.frequency_value.validations.min
                              ),
                            }
                          : undefined,
                        max: frequencyValuesRules.max
                          ? {
                              value: frequencyValuesRules.max,
                              message: t(
                                translations.periodicity_form.fields.frequency_value.validations
                                  .max,
                                {value: frequencyValuesRules.max}
                              ),
                            }
                          : 1,
                      }}
                      render={({field}) => {
                        return (
                          <FlatNumericInput
                            {...frequencyValuesRules}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={(_e, value) => field.onChange(Number(value))}
                            errors={frequencyValueErrorMessage && ' '}
                            sx={quantityInputSx}
                          />
                        );
                      }}
                    />
                    <Slide direction="left" in={Boolean(repetitionQuantityErrorMessage)}>
                      <Typography
                        component="label"
                        sx={errorMessageSx}
                        style={{
                          position: 'absolute',
                          bottom: '-16px',
                          width: '300px',
                        }}
                      >
                        {repetitionQuantityErrorMessage}
                      </Typography>
                    </Slide>
                  </Stack>
                  <div>
                    <Typography sx={{...inputLabelSx, flex: 1, ml: 1}}>
                      {t(
                        frequencyValueNumber === 1
                          ? translations.activity_type_posology_schedule_form.fields
                              .repetitionHourlyFrequencyValueHour
                          : translations.activity_type_posology_schedule_form.fields
                              .repetitionHourlyFrequencyValueHours
                      )}
                    </Typography>
                  </div>
                </Stack>
                <Stack flexDirection="row" alignItems="center">
                  <Stack
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    flex={1}
                    alignItems="center"
                    maxWidth="50%"
                    position="relative"
                  >
                    <Typography sx={{...inputLabelSx, flex: 1}}>
                      {t(
                        translations.activity_type_posology_schedule_form.fields.repetition_quantity
                          .label
                      )}
                    </Typography>
                    <Controller
                      name="repetitionQuantity"
                      defaultValue={1}
                      control={control}
                      rules={{
                        required: {
                          value: PosologyScheduleTypeValue === PosologySchedule.Repetition,
                          message: t(
                            translations.activity_type_posology_schedule_form.fields
                              .repetition_quantity.validations.min
                          ),
                        },
                        min: {
                          value: 0,
                          message: t(
                            translations.activity_type_posology_schedule_form.fields
                              .repetition_quantity.validations.min
                          ),
                        },
                      }}
                      render={({field}) => {
                        return (
                          <FlatNumericInput
                            {...frequencyValuesRules}
                            value={field.value}
                            onBlur={field.onBlur}
                            min={0}
                            max={99}
                            step={0.5}
                            onChange={(_e, value) => field.onChange(Number(value))}
                            errors={repetitionQuantityErrorMessage && ' '}
                            sx={quantityInputSx}
                          />
                        );
                      }}
                    />
                    <Slide direction="left" in={Boolean(repetitionQuantityErrorMessage)}>
                      <Typography
                        component="label"
                        sx={errorMessageSx}
                        style={{
                          position: 'absolute',
                          bottom: '-16px',
                          width: '300px',
                        }}
                      >
                        {repetitionQuantityErrorMessage}
                      </Typography>
                    </Slide>
                  </Stack>
                </Stack>
              </Collapse>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#E4E3E3',
                  height: '1px',
                  mt: '10px',
                  mb: '10px',
                }}
              ></Box>
              {/**
               * Periodicity/Frequency options
               */}
              {PosologyScheduleTypeValue !== PosologySchedule.SOS &&
                PosologyScheduleTypeValue !== PosologySchedule.Repetition && (
                  <Stack
                    flexDirection="row"
                    gap={2}
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack flexDirection="row" gap={1} sx={{width: '100%'}}>
                      <Typography
                        sx={{...inputLabelSx, width: '120px', alignSelf: 'center', flex: 1}}
                      >
                        {t(
                          translations.activity_type_posology_schedule_form.fields.frequency_value
                            .label
                        )}
                      </Typography>
                      <Controller
                        name="frequencyValue"
                        defaultValue={1}
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: t(
                              translations.periodicity_form.fields.frequency_value.validations
                                .required
                            ),
                          },
                          min: frequencyValuesRules.min
                            ? {
                                value: frequencyValuesRules.min,
                                message: t(
                                  translations.periodicity_form.fields.frequency_value.validations
                                    .min
                                ),
                              }
                            : undefined,
                          max: frequencyValuesRules.max
                            ? {
                                value: frequencyValuesRules.max,
                                message: t(
                                  translations.periodicity_form.fields.frequency_value.validations
                                    .max,
                                  {value: frequencyValuesRules.max}
                                ),
                              }
                            : 1,
                        }}
                        render={({field}) => {
                          return (
                            <FlatNumericInput
                              {...frequencyValuesRules}
                              value={field.value}
                              onBlur={field.onBlur}
                              onChange={(_e, value) => field.onChange(Number(value))}
                              errors={frequencyValueErrorMessage && ' '}
                              sx={quantityInputSx}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack flexDirection="row" gap={1} sx={{width: '100%'}}>
                      <Controller
                        name="frequency"
                        control={control}
                        defaultValue={Frequency.Daily}
                        rules={{
                          required: {
                            value: true,
                            message: t(
                              translations.periodicity_form.fields.frequency_value.validations
                                .required
                            ),
                          },
                          validate: {
                            genericSchedulerValidations,
                          },
                        }}
                        render={({field}) => (
                          <FlatSelectDropdown
                            {...field}
                            value={frequencyOptions.find((option) => field.value === option.key)}
                            key={`frequency_options_${frequencyOptions.length}`}
                            onChange={(e) => {
                              const frequency = e?.key;

                              field.onChange(e?.key);

                              if (!frequency) return;

                              if (frequency !== Frequency.Weekly) {
                                setValue('days', undefined);
                              }

                              if (frequency === Frequency.Hourly) {
                                setValue('endDate', moment().add(1, 'day').toISOString(), {
                                  shouldValidate: true,
                                });
                              }

                              // endDate cannot be before 7 days distance
                              if (
                                frequency === Frequency.Weekly &&
                                moment(getValues('endDate')).isBefore(moment().add(7, 'days'))
                              ) {
                                setValue('endDate', moment().add(7, 'days').toISOString(), {
                                  shouldValidate: true,
                                });
                              }

                              // endDate cannot be before 1 days distance
                              if (
                                frequency === Frequency.Daily &&
                                moment(getValues('endDate')).isBefore(moment().add(1, 'day'))
                              ) {
                                setValue('endDate', moment().add(1, 'day').toISOString(), {
                                  shouldValidate: true,
                                });
                              }
                            }}
                            options={frequencyOptions}
                            sx={{
                              width: '108px',
                            }}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                )}
              {frequencyValueErrorMessage && (
                <Slide direction="up" in={Boolean(frequencyValueErrorMessage)}>
                  <Typography component="label" sx={errorMessageSx}>
                    {frequencyValueErrorMessage}
                  </Typography>
                </Slide>
              )}
              <Collapse
                in={frequency === Frequency.Weekly}
                sx={{width: '100%', mt: '10px'}}
                unmountOnExit
                mountOnEnter
              >
                <Stack flexDirection="column" gap={2} sx={{width: '100%'}}>
                  <Typography
                    sx={{
                      ...inputLabelSx,
                    }}
                  >
                    {t(translations.periodicity_form.fields.days.label)}:
                  </Typography>
                  <FormGroup
                    row
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    {daysOfTheWeek.map((day) => (
                      <Controller
                        key={day.key}
                        name="days"
                        control={control}
                        render={({field}) => {
                          const checked = field.value?.includes(day.key) || false;

                          return (
                            <Tooltip key={day.key} title={day.label}>
                              <Box>
                                <Checkbox checked={checked} sx={{display: 'none'}} />
                                <Box
                                  onClick={() => {
                                    const isChecked = !checked;
                                    const selectedDays = field.value || [];

                                    let updatedDays;

                                    if (isChecked) {
                                      updatedDays = [...selectedDays, day.key];
                                    } else {
                                      const dayIndex = selectedDays.indexOf(day.key);
                                      if (dayIndex !== -1) {
                                        updatedDays = [
                                          ...selectedDays.slice(0, dayIndex),
                                          ...selectedDays.slice(dayIndex + 1),
                                        ];
                                      } else {
                                        updatedDays = selectedDays;
                                      }
                                    }

                                    field.onChange(updatedDays);
                                  }}
                                  sx={{
                                    display: 'grid',
                                    placeItems: 'center',
                                    cursor: 'pointer',
                                    height: '28px',
                                    width: '28px',
                                    fontSize: '14px',
                                    borderRadius: '100%',
                                    color: checked
                                      ? 'var(--dcm-blue-base)'
                                      : 'var(--dcm-gray-general)',
                                    backgroundColor: checked
                                      ? 'var(--dcm-blue-lighter)'
                                      : 'var(--dcm-gray-lightbg)',
                                    userSelect: 'none',
                                  }}
                                >
                                  {day.label.substring(0, 1)}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        }}
                      />
                    ))}
                  </FormGroup>
                </Stack>
              </Collapse>
              <Stack flexDirection="row" gap="4px">
                <Typography
                  sx={{
                    ...inputLabelSx,
                    width: '80px',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                >
                  {t(translations.periodicity_form.fields.end_date.label)}:
                </Typography>
                <Controller
                  name="endDate"
                  defaultValue={moment().add(2, 'days').toString()}
                  control={control}
                  rules={{
                    validate: {
                      genericSchedulerValidations,
                    },
                    required: {
                      value: true,
                      message: t(translations.dynamic_form.required_message),
                    },
                  }}
                  render={({field, fieldState: {error}}) => {
                    return (
                      // @ts-ignore
                      <FlatStartsAtDate
                        {...field}
                        shouldDisableDate={(date) => {
                          const minimumPossibleEndDate = moment(startAtDateValue).add({
                            ...(getValues('frequency') === Frequency.Daily && {
                              days: getValues('frequencyValue'),
                            }),
                            ...(getValues('frequency') === Frequency.Weekly && {
                              weeks: getValues('frequencyValue'),
                            }),
                          });

                          const endDateIsPreviousToTheDefinedFrequency =
                            moment(date).isBefore(minimumPossibleEndDate);

                          if (endDateIsPreviousToTheDefinedFrequency) return true;

                          return false;
                        }}
                        sx={{
                          width: '137px',
                          flexShrink: 0,
                          '.MuiInputBase-root': {
                            gap: '0px',
                          },
                          'span.input-error': {
                            bottom: '-18px !important',
                          },
                        }}
                        errors={endDateErrorMessage}
                      />
                    );
                  }}
                />

                <Stack flexDirection="row" gap="10px" marginLeft="6px" alignItems="center">
                  <Typography sx={{...inputLabelSx, flexShrink: 0}}>
                    {t(translations.activity_type_posology_schedule_form.fields.end_hour.label)}:
                  </Typography>
                  <Controller
                    name="endHour"
                    defaultValue={moment().toString()}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t(translations.dynamic_form.required_message),
                      },
                      validate: {
                        genericSchedulerValidations,
                      },
                    }}
                    render={({field}) => (
                      <FlatStartsAtTime
                        {...field}
                        // @ts-ignore
                        value={moment(field.value)}
                        shouldDisableTime={(time) => {
                          if (startAtTimeValue && startAtDateValue && time) {
                            const startAndEndDatesAreTheSame = moment(startAtDateValue).isSame(
                              moment(getValues('endDate')),
                              'days'
                            );
                            const endDateIsPreviousToTheDefinedFrequency = moment(startAtTimeValue)
                              .add(getValues('frequencyValue'), 'hours')
                              .isAfter(time);

                            if (
                              startAndEndDatesAreTheSame &&
                              endDateIsPreviousToTheDefinedFrequency
                            ) {
                              return true;
                            }
                          }

                          return false;
                        }}
                        required
                        errors={endHourErrorMessage}
                        sx={{width: '115px'}}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
        </Modal>
      </Box>
    );
  }
);

// ---------------------------------------------------------------
// ---------------------------------------------------------------
// ---------------------------------------------------------------
const PeriodsOfTheDayQuantityField = memo(PeriodsOfTheDayQuantityFieldImpl);

type PeriodsOfTheDayQuantityFieldProps = {
  activity: {
    id?: string;
    name?: string;
    description?: string;
  };
  index: number;
  control: Control<PosologyScheduleFormType>;
  hasError: boolean;
  clearSectionError: () => void;
};

function PeriodsOfTheDayQuantityFieldImpl({
  activity,
  index,
  control,
  hasError,
  clearSectionError,
}: PeriodsOfTheDayQuantityFieldProps) {
  const {field} = useController({
    name: `periodsOfTheDay.${index}.quantity`,
    control,
  });

  return (
    <Stack sx={periodsOfTheDayInputWrapper}>
      <Stack
        sx={{
          width: '100%',
        }}
      >
        <Tooltip title={activity.description}>
          <Typography
            sx={{
              ...inputLabelSx,
              maxWidth: '130px',
            }}
            noWrap
          >
            {activity.name}
          </Typography>
        </Tooltip>
      </Stack>

      <FlatNumericInput
        min={0}
        max={99}
        step={0.5}
        value={field.value}
        onBlur={field.onBlur}
        onChange={(_event, value) => {
          field.onChange(value ?? 0);

          if (hasError && Number(value ?? 0) > 0) {
            clearSectionError();
          }
        }}
        sx={quantityInputSx}
        errors={hasError ? '' : undefined}
      />
    </Stack>
  );
}

const modalSx: SxProps<Theme> = {
  width: '484px',
};

const modalContentWrapperSx: SxProps<Theme> = {
  width: '100%',
  gap: '20px',
  display: 'flex',
};

const periodsOfTheDayInputWrapper: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  width: 'calc(50% - 8px)',
  alignItems: 'center',
  alignContent: 'flex-start',
  gap: '10px',
  justifyContent: 'space-between',
};

const hourIconSx: SxProps = {
  color: 'var(--dcm-gray)',
  width: '24px',
  height: '24px',
};

const inputLabelSx: SxProps<Theme> = {
  color: 'var(--dcm-gray)',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 500,
};

const quantityInputSx: SxProps<Theme> = {
  width: '50px',
  flexShrink: 0,
};

const timesListChipSx: SxProps<Theme> = {
  padding: '0px 2px 0px 4px',
  borderRadius: '5px',
  background: 'var(--dcm-blue-lighter)',
  ':hover': {
    filter: 'brightness(95%)',
  },
  '.MuiChip-icon': {
    color: 'var(--dcm-gray)',
    height: '18px',
    width: '18px',
  },
  '.MuiChip-label': {
    color: 'var(--dcm-darkgray)',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '150%',
    letterSpacing: '0.14px',
  },
  '.MuiChip-deleteIcon': {
    color: 'var(--dcm-darkgray)',
    width: '28px',
    height: '28px',
    padding: '7px',
  },
};

const errorMessageSx: SxProps<Theme> = {
  color: 'var(--dcm-red-base)',
  fontWeight: '500',
  fontSize: '12px',
  lineHeight: '150%',
  marginTop: '2px',
};

const daysOfTheWeek = getDaysOfTheWeek();
